/**
 * The token formula editor, as AgencyAnalytics': the formula is a row of
 * chips; under it, Metrics (or Columns, inside a view), Operators (+ − × ÷
 * and brackets) and Numbers add a chip at the end. Backspace removes the last
 * chip. The server checks the formula again before saving.
 */

import { useState, type ReactNode } from 'react';

import type { CatalogueSource } from '../reports/reports-api.ts';
import type { FormulaToken } from './custom-api.ts';

const OPERATORS: { op: '+' | '-' | '*' | '/' | '(' | ')'; label: string; name: string }[] = [
  { op: '+', label: '+', name: 'plus' },
  { op: '-', label: '−', name: 'minus' },
  { op: '*', label: '×', name: 'times' },
  { op: '/', label: '÷', name: 'divided by' },
  { op: '(', label: '(', name: 'open bracket' },
  { op: ')', label: ')', name: 'close bracket' },
];

export type FormulaChoices =
  | { kind: 'metrics'; sources: CatalogueSource[] }
  | { kind: 'columns'; columns: { id: string; name: string }[] };

export function tokenText(token: FormulaToken, choices: FormulaChoices): string {
  if (token.kind === 'number') return String(token.value);
  if (token.kind === 'op') return OPERATORS.find((item) => item.op === token.op)?.label ?? token.op;
  if (token.kind === 'column')
    return choices.kind === 'columns'
      ? (choices.columns.find((column) => column.id === token.column)?.name ?? 'Missing column')
      : token.column;
  const source =
    choices.kind === 'metrics'
      ? choices.sources.find((item) => item.key === token.source)
      : undefined;
  const metric = source?.metrics.find((item) => item.key === token.metric);
  return `${source?.label ?? token.source}: ${metric?.label ?? token.metric}`;
}

export function formulaText(tokens: FormulaToken[], choices: FormulaChoices): string {
  return tokens.map((token) => tokenText(token, choices)).join(' ');
}

export function FormulaEditor(props: {
  tokens: FormulaToken[];
  choices: FormulaChoices;
  onChange: (tokens: FormulaToken[]) => void;
}): ReactNode {
  const { tokens, choices, onChange } = props;
  const firstSource = choices.kind === 'metrics' ? (choices.sources[0]?.key ?? '') : '';
  const [sourceKey, setSourceKey] = useState(firstSource);
  const source =
    choices.kind === 'metrics' ? choices.sources.find((item) => item.key === sourceKey) : undefined;
  const [metricKey, setMetricKey] = useState(source?.metrics[0]?.key ?? '');
  const [columnId, setColumnId] = useState(
    choices.kind === 'columns' ? (choices.columns[0]?.id ?? '') : '',
  );
  const [number, setNumber] = useState('');
  // Columns can be added after the editor opens: fall back to the first one.
  const column = columnId || (choices.kind === 'columns' ? (choices.columns[0]?.id ?? '') : '');
  const push = (token: FormulaToken) => onChange([...tokens, token]);

  return (
    <div className="fe">
      <div
        className="fe-line"
        role="group"
        aria-label="Formula"
        tabIndex={0}
        onKeyDown={(event) => event.key === 'Backspace' && onChange(tokens.slice(0, -1))}
      >
        {tokens.length === 0 && (
          <span className="kp-faint">Add a metric to start the formula.</span>
        )}
        {tokens.map((token, index) => (
          <span key={index} className={`fe-chip fe-chip-${token.kind}`}>
            {tokenText(token, choices)}
          </span>
        ))}
        {tokens.length > 0 && (
          <button
            type="button"
            className="ex-link"
            aria-label="Remove the last piece"
            onClick={() => onChange(tokens.slice(0, -1))}
          >
            ⌫
          </button>
        )}
      </div>
      <div className="fe-tools">
        <fieldset className="fe-group">
          <legend>{choices.kind === 'metrics' ? 'Metrics' : 'Columns'}</legend>
          {choices.kind === 'metrics' ? (
            <>
              <select
                aria-label="Integration"
                value={sourceKey}
                onChange={(event) => {
                  setSourceKey(event.target.value);
                  setMetricKey(
                    choices.sources.find((item) => item.key === event.target.value)?.metrics[0]
                      ?.key ?? '',
                  );
                }}
              >
                {choices.sources.map((item) => (
                  <option key={item.key} value={item.key}>
                    {item.label}
                  </option>
                ))}
              </select>
              <select
                aria-label="Metric"
                value={metricKey}
                onChange={(event) => setMetricKey(event.target.value)}
              >
                {source?.metrics.map((item) => (
                  <option key={item.key} value={item.key}>
                    {item.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn secondary"
                disabled={!metricKey}
                onClick={() => push({ kind: 'metric', source: sourceKey, metric: metricKey })}
              >
                Add
              </button>
            </>
          ) : (
            <>
              <select
                aria-label="Column"
                value={column}
                onChange={(event) => setColumnId(event.target.value)}
              >
                {choices.columns.map((column) => (
                  <option key={column.id} value={column.id}>
                    {column.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn secondary"
                disabled={!column}
                onClick={() => push({ kind: 'column', column })}
              >
                Add
              </button>
            </>
          )}
        </fieldset>
        <fieldset className="fe-group">
          <legend>Operators</legend>
          {OPERATORS.map((item) => (
            <button
              key={item.op}
              type="button"
              className="fe-op"
              aria-label={item.name}
              onClick={() => push({ kind: 'op', op: item.op })}
            >
              {item.label}
            </button>
          ))}
        </fieldset>
        <fieldset className="fe-group">
          <legend>Numbers</legend>
          <input
            type="number"
            inputMode="decimal"
            aria-label="Number"
            value={number}
            onChange={(event) => setNumber(event.target.value)}
          />
          <button
            type="button"
            className="btn secondary"
            disabled={number.trim() === '' || !Number.isFinite(Number(number))}
            onClick={() => {
              push({ kind: 'number', value: Number(number) });
              setNumber('');
            }}
          >
            Add
          </button>
        </fieldset>
      </div>
    </div>
  );
}
